import { useUser } from "@clerk/expo";
import * as ImagePicker from "expo-image-picker";
import { Check, CornerDownRight, Plus, Square, X } from "lucide-react-native";
import { FC, useState } from "react";

import {
  Button,
  ButtonText,
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
  HStack,
  Input,
  InputField,
  MediaPicker,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Question, QuestionDraft, Rule } from "@/types/resources";
import { QuestionSuggestion } from "@/types/responses";
import { RuleOperator } from "@/types/utils";
import {
  CarryForwardField,
  RuleEditor,
  RulesEditor,
  conditionSourceQuestions,
  hasApplicableRules,
} from "../rules";
import AdvancedSettings from "./AdvancedSettings";
import QuestionField from "./QuestionField";
import QuestionTextInput from "./QuestionTextInput";

const MAX_OPTIONS = 15;
const MIN_OPTIONS = 2;

interface MultipleChoiceQuestionEditorProps {
  allQuestions?: Question[];
  /** Rules are only persisted on update, so the create form hides them. */
  canEditRules?: boolean;
  currentQuestionOrder?: number;
  disabled?: boolean;
  onChange: (question: QuestionDraft<"multiple_choice">) => void;
  question: QuestionDraft<"multiple_choice">;
  setUseImages: (useImages: boolean) => void;
  useImages: boolean;
}

const MultipleChoiceQuestionEditor: FC<MultipleChoiceQuestionEditorProps> = ({
  allQuestions = [],
  canEditRules = true,
  currentQuestionOrder,
  disabled = false,
  onChange,
  question,
  setUseImages,
  useImages,
}) => {
  const { i18n } = useLocalization();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.admin === true;
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const isDisabled = disabled || isSuggesting;

  const rules = question.rules || [];

  // Direct rules bound to a specific option (membership of that option in the
  // answer), as opposed to cross-question conditional rules.
  const optionSpecificRules = rules.filter(
    (r) => r.option_index !== null && r.source_question_id === null
  );

  // Carry-forward ports the options from a prior question, hiding the manual
  // option editor (and image toggle) while active.
  const carriedActive = !!question.carry_forward;

  const previousConditionQuestions = conditionSourceQuestions(
    allQuestions,
    currentQuestionOrder
  );

  const handleQuestionTextChange = (text: string) => {
    onChange({ ...question, text });
  };

  const handleSuggest = (suggestion: QuestionSuggestion) => {
    onChange({
      ...question,
      options:
        suggestion.options.length > 0 ? suggestion.options : question.options,
      text: suggestion.text,
    });
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...question.options];
    newOptions[index] = value;
    onChange({ ...question, options: newOptions });
  };

  const addOption = () => {
    if (isAdmin || question.options.length < MAX_OPTIONS) {
      onChange({ ...question, options: [...question.options, ""] });
    }
  };

  const handleUseImagesChange = (checked: boolean) => {
    setUseImages(checked);
    onChange({ ...question, options: checked ? [] : ["", ""] });
  };

  const handleImagesChange = (
    newImages: (ImagePicker.ImagePickerAsset | string)[]
  ) => {
    onChange({ ...question, media: newImages });
  };

  const removeOption = (index: number) => {
    if (question.options.length > MIN_OPTIONS) {
      // Drop rules for this option and shift higher option indices down.
      const newRules = rules
        .filter(
          (rule) =>
            !(rule.option_index === index && rule.source_question_id === null)
        )
        .map((rule) => ({
          ...rule,
          option_index:
            rule.option_index !== null &&
            rule.source_question_id === null &&
            rule.option_index > index
              ? rule.option_index - 1
              : rule.option_index,
        }));
      onChange({
        ...question,
        options: question.options.filter((_, i) => i !== index),
        rules: newRules,
      });
    }
  };

  const handleRulesChange = (newRules: Rule[]) => {
    onChange({ ...question, rules: newRules });
  };

  const getRuleForOption = (optionIndex: number): Rule | undefined =>
    optionSpecificRules.find((rule) => rule.option_index === optionIndex);

  const getTargetQuestionIdForOption = (optionIndex: number): string | null => {
    const rule = getRuleForOption(optionIndex);
    if (!rule) return null;
    if (rule.target_question_id === null) return "__END_SURVEY__";
    return rule.target_question_id;
  };

  const handleRuleSave = (
    targetQuestionId: string | null,
    operator: RuleOperator
  ) => {
    if (editingRuleIndex === null) return;

    const existingRule = getRuleForOption(editingRuleIndex);
    const otherRules = rules.filter(
      (r) =>
        !(r.option_index === editingRuleIndex && r.source_question_id === null)
    );

    if (targetQuestionId === null) {
      onChange({ ...question, rules: otherRules });
    } else {
      const newRule: Rule = {
        created_at: existingRule?.created_at || "",
        id: existingRule?.id || "",
        operator,
        option_index: editingRuleIndex,
        probability: null,
        source_question_id: null,
        target_question_id:
          targetQuestionId === "__END_SURVEY__" ? null : targetQuestionId,
        updated_at: new Date().toISOString(),
      };
      onChange({ ...question, rules: [...otherRules, newRule] });
    }

    setEditingRuleIndex(null);
  };

  const handleRuleOperatorChange = (operator: RuleOperator) => {
    if (editingRuleIndex === null) return;
    onChange({
      ...question,
      rules: rules.map((r) =>
        r.option_index === editingRuleIndex && r.source_question_id === null
          ? { ...r, operator, updated_at: new Date().toISOString() }
          : r
      ),
    });
  };

  const canShowRules =
    allQuestions.length > 1 && currentQuestionOrder !== undefined;

  return (
    <VStack className="gap-6">
      <QuestionField order={currentQuestionOrder}>
        <QuestionTextInput
          disabled={disabled}
          lines={question.lines}
          onChange={handleQuestionTextChange}
          onLoadingChange={setIsSuggesting}
          onSuggest={handleSuggest}
          options={question.options}
          text={question.text || ""}
          type={question.type}
        />
      </QuestionField>

      {!useImages && (
        <VStack className="gap-3">
          <Text className="text-sm font-semibold text-typography-800">
            {i18n.t("questions.optionsLabel")}
          </Text>
          <CarryForwardField
            allQuestions={allQuestions}
            currentQuestionOrder={currentQuestionOrder}
            disabled={isDisabled}
            manualLabel={i18n.t("questions.carryForward.modeManualOptions")}
            onChange={({ carry_forward, items }) =>
              onChange({
                ...question,
                carry_forward,
                ...(items !== null ? { options: items } : {}),
              })
            }
            target="options"
            value={question.carry_forward}
          >
            <VStack className="gap-3">
              <Text className="text-xs text-typography-400">
                {i18n.t("questions.minOptionsHint", { count: MIN_OPTIONS })}
              </Text>
              <VStack className="gap-2 pl-4">
                {question.options.map((option, index) => {
                  const hasRule = !!getRuleForOption(index);

                  return (
                    <HStack key={index} className="items-center gap-3">
                      <Square className="text-typography-400" size={16} />
                      <Input
                        className="flex-1 border border-outline-200 rounded-xl bg-background-50"
                        isDisabled={isDisabled}
                      >
                        <InputField
                          onChangeText={(value) =>
                            handleOptionChange(index, value)
                          }
                          placeholder={i18n.t("questions.enterOption")}
                          value={option}
                        />
                      </Input>
                      {canShowRules && (
                        <Pressable
                          className="p-2"
                          disabled={isDisabled}
                          onPress={() => setEditingRuleIndex(index)}
                        >
                          <CornerDownRight
                            className={
                              hasRule
                                ? "text-primary-600"
                                : "text-typography-300"
                            }
                            size={18}
                          />
                        </Pressable>
                      )}
                      {question.options.length > MIN_OPTIONS && (
                        <Pressable
                          className="p-1.5 rounded-md hover:bg-error-50"
                          disabled={isDisabled}
                          onPress={() => removeOption(index)}
                        >
                          <X
                            color="rgb(var(--color-typography-500))"
                            size={16}
                          />
                        </Pressable>
                      )}
                    </HStack>
                  );
                })}

                {(isAdmin || question.options.length < MAX_OPTIONS) && (
                  <Button
                    className="self-start rounded-lg border-dashed"
                    isDisabled={isDisabled}
                    onPress={addOption}
                    size="sm"
                    variant="outline"
                  >
                    <Plus color="rgb(var(--color-typography-600))" size={14} />
                    <ButtonText className="text-sm text-typography-600 ml-1">
                      {i18n.t("questions.addOption")}
                    </ButtonText>
                  </Button>
                )}
              </VStack>
            </VStack>
          </CarryForwardField>
        </VStack>
      )}

      {!carriedActive && (
        <>
          <MediaPicker
            hint={
              useImages
                ? i18n.t("questions.minMediaHint", { count: MIN_OPTIONS })
                : undefined
            }
            isDisabled={isDisabled}
            maxItems={MAX_OPTIONS}
            media={question.media}
            onMediaChange={handleImagesChange}
          />

          <Checkbox
            isChecked={useImages}
            isDisabled={isDisabled}
            onChange={handleUseImagesChange}
            value="useImages"
          >
            <CheckboxIndicator className="data-[checked=true]:bg-primary-500 data-[checked=true]:border-primary-500">
              <CheckboxIcon as={Check} className="text-white" size="sm" />
            </CheckboxIndicator>
            <CheckboxLabel className="text-sm font-medium text-typography-800">
              {i18n.t("questions.useImagesAsOptions")}
            </CheckboxLabel>
          </Checkbox>
        </>
      )}

      {canEditRules &&
        hasApplicableRules(allQuestions, currentQuestionOrder) && (
          <AdvancedSettings defaultExpanded={rules.length > 0}>
            <RulesEditor
              allQuestions={allQuestions}
              currentQuestionOrder={currentQuestionOrder}
              disabled={isDisabled}
              hasOptionSpecificRules={optionSpecificRules.length > 0}
              onRulesChange={handleRulesChange}
              previousConditionQuestions={previousConditionQuestions}
              rules={rules}
            />
          </AdvancedSettings>
        )}

      {editingRuleIndex !== null && (
        <RuleEditor
          allQuestions={allQuestions}
          currentQuestionOrder={currentQuestionOrder}
          initialOperator={
            getRuleForOption(editingRuleIndex)?.operator ?? RuleOperator.In
          }
          isOpen={editingRuleIndex !== null}
          onClose={() => setEditingRuleIndex(null)}
          onOperatorChange={handleRuleOperatorChange}
          onSave={handleRuleSave}
          optionText={question.options[editingRuleIndex] || ""}
          questionType="multiple_choice"
          targetQuestionId={getTargetQuestionIdForOption(editingRuleIndex)}
        />
      )}
    </VStack>
  );
};

export default MultipleChoiceQuestionEditor;
